import {
    SAMPLER,
    MIDI, AUDIO,
    INSTRUMENT,
    MULTI_FREEZE,
    INSTRUMENT_RACK} from 'constants/track-types.js';
import {loadSamplersIntoTrack} from './loadSamplersIntoTrack.js';
import StepData from 'pattern/StepData.js';
import StepBucket from 'pattern/StepBucket.js';
import Pattern from 'pattern/Pattern.js';
import EffectsChain from 'effects/EffectsChain.js';
import {getEffect} from 'effects/EffectsList.js';
import ParameterContext from 'effects/ParameterContext.js';
import {getDefaultEffects} from 'effects/getDefaultEffects.js';
import {constructTrack} from 'track/constructTrack.js';
import Track from 'track/Track.js';
import MutableTrack from 'MutableTrack.js';
import MidiTrack from 'MidiTrack.js';
import MultiFreezeTrack from 'MultiFreezeTrack.js';
import AudioTrack from 'AudioTrack.js';
import InstrumentTrack from 'track/InstrumentTrack.js';
import MultiSynthTrack from 'track/MultiSynthTrack.js';
import FMSynthTrack from 'track/FMSynthTrack.js';
import {setEffectParameterValues} from 'actions/index.js';
import SampledInstrumentTrack from 'track/SampledInstrumentTrack.js';

const BASE_RESOLUTION =  Track.baseResolution;

export const loadCompleteTrackJson = async (
    {
        sequencerEngine,
        trackData,
        patterns,
        trackNumber, // the track number it should be at..
        subSequence,
        patternParameters,
        tracks, // the current tracks in the sequence (used for effects)
        trackEffects,
        trackMidiEffects,
        trackEffectChains,
        patternToLoad,
        numPatterns
    }) => {
        let track  = await loadTrackJson(sequencerEngine, trackData, patterns, trackNumber, subSequence);
        await loadTrackPatternsAndEffects({
            sequencerEngine,
            track,
            tracks,
            patterns,
            trackData,
            trackEffects,
            trackMidiEffects,
            patternParameters,
            trackEffectChains,
            subSequence,
            numPatterns,
            patternToLoad
        });
        track.loadLostParameters();
        return track;
}

// loads the basics of the track (no effects or patterns though);
export const loadTrackJson = async (engine, trackData, patterns, trackNumber, subSequence, numPatterns=patterns.length) => {
    let isMidi = 'type' in trackData && trackData.type === 'MIDI';
    let type = trackData.type;
    let track = undefined; 
    if (type === MIDI) {
        track = new MidiTrack(
            trackNumber, engine, engine.mixSession,
            numPatterns, () => engine.update(), engine.sends);
    } else if (type === SAMPLER) {
        track = new MutableTrack(
            trackNumber,
            engine,
            engine.mixSession,
            numPatterns,
            () => engine.update(),
            subSequence);
    } else if (type === AUDIO) {
        track = new AudioTrack(
            trackNumber,
            engine,
            engine.mixSession,
            numPatterns,
            () => engine.update(),
            subSequence);
    } else if (type === MULTI_FREEZE) {
        track = new MultiFreezeTrack(
            trackNumber,
            engine,
            engine.mixSession,
            numPatterns,
            () => engine.update(),
            subSequence);
    } else if (type === INSTRUMENT || type === INSTRUMENT_RACK) {
        let instrumentType = trackData.instrumentType;
        track = constructTrack(engine, type, instrumentType);
    } 
    track.setSends(engine.sends);
    let bulk = await track.fromJson(trackData);
    if (bulk) {
        track.sequencerEngine.dispatch(
            setEffectParameterValues(bulk));
    }

    // todo: need to hook up chokes
    return track;
};

export const loadTrackPatternsAndEffects = async ({
    sequencerEngine,
    track,
    tracks,
    patterns,
    trackData,
    patternParameters,
    trackEffects,
    trackMidiEffects,
    trackEffectChains,
    subSequence,
    numPatterns=patterns.length,
    patternToLoad // optional argument if we want to load a specific pattern
}) => {
    var maxLengthOfPattern = -16;
    var patternsLoaded = [];
    let bulk = {};
    console.log("loading track effects");
    let audioFX  = await loadTrackEffects({
        sequencerEngine,
        trackEffectChains,
        trackEffects,
        track,
        trackNumber:
        track.trackNumber,
        tracks});
    let effects = audioFX.effects;
    console.log("loading midi effects");
    let midiEffectsRtr = loadTrackMidiEffects(trackMidiEffects, track);
    let midiEffects = midiEffectsRtr.effects;
    bulk = {
        ... bulk,
        ... audioFX.bulk,
        ... midiEffectsRtr.bulk
    };
    
    console.log("loading buckets");
    // go through each pattern in the track
    for (var p=0; p < numPatterns; p++) {
        var stepBuckets = [];
        var pattern = patterns[patternToLoad || p];
        
        //g go through each step and load the step data into the track
        var size = 'size' in pattern ? pattern.size : BASE_RESOLUTION;
        if (size > maxLengthOfPattern) {
            maxLengthOfPattern = size;
        }
        
        var buckets = 'buckets' in pattern;
        var patternObj = new Pattern(sequencerEngine, track);
        patternObj.changeLength(size, false);

        for (var j=0; j < size; j++) {
            if (!(j in pattern)) {
                let bucket = patternObj.stepBuckets[j];
                stepBuckets.push(bucket);
                continue;
            }
            
            var stepBucket = patternObj.stepBuckets[j];
            var jsonStepBucket = pattern[j];

            // fill the steps for this buckets using the json's data
            // Each step has several parameters, that need to be loaded
            // into a StepData object
            
            if (!buckets) {
                var step = stepBucket.steps[0];
                step.track = track;
                step.stepNumber = j;
                step.simpleLoad(pattern[j], effects, track, midiEffects);
                stepBucket.steps[0] = step;
            } else {
                for (var key in jsonStepBucket) {
                    var stepData = stepBucket.steps[key] || new StepData();
                    stepData.track = track;
                    stepData.stepNumber = j;
                    stepData.simpleLoad(
                        jsonStepBucket[key], effects, track, midiEffects);
                    stepBucket.steps[key] = stepData;
                }
            }
            
            stepBuckets.push(stepBucket);
        }
        
        patternObj.loadStepBuckets(undefined, true);
        if (patternParameters) {
            patternObj.loadFromJson(patternParameters[p]);
        }
        patternsLoaded.push(patternObj);
    }
    
    track.patterns = patternsLoaded;
    track.pattern.muted = trackData.muted;
    track.gated = trackData.gated;

    console.log("loading samplers into track");
    await loadSamplersIntoTrack(track, trackData, subSequence);
    track.setupEffects();

    sequencerEngine.dispatch(
        setEffectParameterValues(bulk));
};

export const loadTrackEffects = async ({
    sequencerEngine,
    track,
    trackEffects,
    trackEffectChains,
    trackNumber,
    tracks
}) => {
    
    let bulk = {};
    let effects = [];
    let effectsChain = track.effectsChains[0];

    if (trackEffects !== undefined) {
        for (let effect of trackEffects) {
            effects.push(await getEffect(
                effect, tracks, sequencerEngine, trackNumber));
        }
        effectsChain.updateEffects(effects.map(x => x.effect));
    } else if (trackEffectChains !== undefined) {
        let effectsChains = [];
        for (let effectsChainJson of trackEffectChains) {
            let effectsChain = new EffectsChain(
                track.gainNode, track.trackNode, new ParameterContext().forTrack(track), sequencerEngine);
            
            for (let k in effectsChainJson) {
                let effect = effectsChainJson[k];
                console.log('loading effect = ', effect.name);
                let ret = getEffect(effect, tracks, sequencerEngine, trackNumber);
                effectsChain.addEffect(ret.effect);
                bulk = {
                    ... bulk,
                    ... ret.bulk
                };
            }
            effectsChain.updateEffects();
            effectsChains.push(effectsChain);
        }
        track.effectsChains.forEach(c => c.dispose());
        track.effectsChains = effectsChains;
    } else {
        effects = getDefaultEffects(sequencerEngine);
        effectsChain.updateEffects(effects);
    }
    
    return {effects, bulk};
};

const  loadTrackMidiEffects = (trackMidiEffects, track) => {
    if (trackMidiEffects !== undefined) {
        let bulk = track.midiEffects.fromJson(trackMidiEffects);
        track.updateMidiEffectsRedux();
        return {
            effects: track.midiEffects.effects,
            bulk
        };
    }
    return {effects: [], bulk: {}};
};

